import React, { useMemo, useState } from "react";
import clsx from "clsx";

import { useGetTimelineQuery } from "@/store/services/capacity/capacity.service";
import { useGetDepartmentsQuery } from "@/store/services/departments/departments.service";
import { useGetTeamsQuery } from "@/store/services/teams/teams.service";
import { useGetProjectsQuery } from "@/store/services/projects/projects.service";
import {
    useCreateBookingMutation,
    useUpdateBookingMutation,
    useDeleteBookingMutation,
} from "@/store/services/bookings/bookings.service";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { BookingModal, type BookingModalState } from "./booking-modal";
import { IndividualRows, type Column } from "./individual-rows";
import { BudgetCoveragePanel } from "./budget-coverage-panel";

type ViewMode = "monthly" | "quarterly" | "yearly";

// ─── column builders ─────────────────────────────────────────────────────────

function ym(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, "0")}`;
}

function buildColumns(viewMode: ViewMode, offset: number): Column[] {
    const now = new Date();

    if (viewMode === "monthly") {
        const cols: Column[] = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() + offset + i, 1);
            const key = ym(d.getFullYear(), d.getMonth() + 1);
            cols.push({
                key,
                label: d.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
                months: [key],
                monthWeights: { [key]: 1 },
            });
        }
        return cols;
    }

    if (viewMode === "quarterly") {
        const cols: Column[] = [];
        const startQ = Math.floor(now.getMonth() / 3) + offset;
        for (let i = 0; i < 6; i++) {
            const totalQ = startQ + i;
            const year = now.getFullYear() + Math.floor(totalQ / 4);
            const q = ((totalQ % 4) + 4) % 4;
            const months: string[] = [];
            for (let m = 0; m < 3; m++) months.push(ym(year, q * 3 + m + 1));
            const weights: Record<string, number> = {};
            for (const m of months) weights[m] = 1;
            cols.push({ key: `${year}-Q${q + 1}`, label: `Q${q + 1} ${year}`, months, monthWeights: weights });
        }
        return cols;
    }

    // yearly
    const cols: Column[] = [];
    const startYear = now.getFullYear() + offset;
    for (let y = startYear; y < startYear + 3; y++) {
        const months: string[] = [];
        for (let m = 1; m <= 12; m++) months.push(ym(y, m));
        const weights: Record<string, number> = {};
        for (const m of months) weights[m] = 1;
        cols.push({ key: String(y), label: String(y), months, monthWeights: weights });
    }
    return cols;
}

// ─── aggregation helpers ──────────────────────────────────────────────────────

type ColumnCapacitySummary = {
    workingDays: number;
    grossCapacityMd: number;
    absencesMd: number;
    totalCapacityMd: number;
    totalBookingsMd: number;
    gapMd: number;
};

function computeCapacitySummary(
    individuals: {
        utilisation: {
            month: string;
            workingDays: number;
            grossCapacityMd: number;
            effectiveCapacityFte: number;
            bookedFte: number;
        }[];
    }[],
    col: Column,
): ColumnCapacitySummary {
    const seenMonths = new Set<string>();
    let workingDays = 0;
    let grossCapacityMd = 0;
    let totalCapacityMd = 0;
    let totalBookingsMd = 0;

    for (const ind of individuals) {
        const byMonth = Object.fromEntries(ind.utilisation.map((u) => [u.month.slice(0, 7), u]));
        for (const m of col.months) {
            const u = byMonth[m];
            if (!u) continue;
            if (!seenMonths.has(m)) {
                workingDays += u.workingDays;
                seenMonths.add(m);
            }
            grossCapacityMd += u.grossCapacityMd;
            totalCapacityMd += u.effectiveCapacityFte * u.workingDays;
            totalBookingsMd += u.bookedFte * u.effectiveCapacityFte * u.workingDays;
        }
    }

    return {
        workingDays,
        grossCapacityMd,
        absencesMd: grossCapacityMd - totalCapacityMd,
        totalCapacityMd,
        totalBookingsMd,
        gapMd: totalCapacityMd - totalBookingsMd,
    };
}

function columnsToDateRange(columns: Column[]): { from: string; to: string } {
    const allMonths = [...new Set(columns.flatMap((c) => c.months))].sort();
    const first = allMonths[0];
    const last = allMonths[allMonths.length - 1];
    const [ly, lm] = last.split("-").map(Number);
    const lastDay = new Date(ly, lm, 0).toISOString().slice(0, 10);
    return { from: `${first}-01`, to: lastDay };
}

// ─── constants ────────────────────────────────────────────────────────────────

const PROJECT_COLORS = [
    "bg-blue-200 text-blue-800",
    "bg-green-200 text-green-800",
    "bg-purple-200 text-purple-800",
    "bg-orange-200 text-orange-800",
    "bg-pink-200 text-pink-800",
    "bg-teal-200 text-teal-800",
];

// ─── page component ───────────────────────────────────────────────────────────

export const TimelinePage = () => {
    const [departmentId, setDepartmentId] = useState<string>("all");
    const [teamId, setTeamId] = useState<string>("all");
    const [projectId, setProjectId] = useState<string>("all");
    const [groupByTeam, setGroupByTeam] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>("monthly");
    const [offset, setOffset] = useState(0);
    const [modalState, setModalState] = useState<BookingModalState | null>(null);

    const columns = useMemo(() => buildColumns(viewMode, offset), [viewMode, offset]);
    const { from, to } = useMemo(() => columnsToDateRange(columns), [columns]);

    const { data: departments } = useGetDepartmentsQuery();
    const { data: teams } = useGetTeamsQuery(departmentId !== "all" ? { departmentId } : undefined);
    const { data: projects } = useGetProjectsQuery();
    const { data: timeline, isLoading } = useGetTimelineQuery({
        from,
        to,
        departmentId: departmentId !== "all" ? departmentId : undefined,
        teamId: teamId !== "all" ? teamId : undefined,
    });
    const [createBooking] = useCreateBookingMutation();
    const [updateBooking] = useUpdateBookingMutation();
    const [deleteBooking] = useDeleteBookingMutation();

    const projectColorMap: Record<string, string> = {};
    projects?.forEach((p, i) => {
        projectColorMap[p.id] = PROJECT_COLORS[i % PROJECT_COLORS.length];
    });

    const individuals = timeline?.individuals ?? [];

    const grouped: { teamName: string; teamId: string; rows: typeof individuals }[] = [];
    if (groupByTeam) {
        const teamMap = new Map<string, typeof individuals>();
        for (const ind of individuals) {
            const existing = teamMap.get(ind.teamId) ?? [];
            existing.push(ind);
            teamMap.set(ind.teamId, existing);
        }
        for (const [tid, rows] of teamMap) {
            grouped.push({ teamId: tid, teamName: rows[0]?.teamName ?? tid, rows });
        }
    }

    // ─── handlers ─────────────────────────────────────────────────────────────

    const handleModalConfirm = async (
        serviceId: string,
        fte: number,
        allocationUnit: "fte_pct" | "fte_fraction",
        startDate: string,
        endDate: string,
    ) => {
        if (!modalState) return;
        const project = (projects ?? []).find((p) => p.budgets.some((b) => b.services.some((c) => c.id === serviceId)));
        if (!project) return;
        if (modalState.mode === "create") {
            await createBooking({
                individualId: modalState.individualId,
                projectId: project.id,
                serviceId,
                startDate,
                endDate,
                fte,
                allocationUnit,
            }).unwrap();
        } else {
            await updateBooking({
                id: modalState.bookingId,
                data: { serviceId, startDate, endDate, fte, allocationUnit },
            }).unwrap();
        }
    };

    const handleModalDelete = async () => {
        if (!modalState || modalState.mode !== "edit") return;
        await deleteBooking(modalState.bookingId).unwrap();
    };

    const activeProjects = (projects ?? []).filter((p) => !p.archived);

    return (
        <div className="space-y-4">
            {/* ── header controls ── */}
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold mr-4">Timeline</h1>

                <div className="flex gap-1 border rounded-md p-0.5 bg-slate-100">
                    {(["monthly", "quarterly", "yearly"] as ViewMode[]).map((m) => (
                        <button
                            key={m}
                            onClick={() => {
                                setViewMode(m);
                                setOffset(0);
                            }}
                            className={clsx(
                                "px-3 py-1 text-sm rounded capitalize",
                                viewMode === m ? "bg-white shadow font-medium" : "text-slate-500",
                            )}
                        >
                            {m}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setOffset((o) => o - 1)}
                        className="px-2 py-1 text-sm border rounded hover:bg-slate-100"
                        title="Navigate backward"
                    >
                        ‹
                    </button>
                    {offset !== 0 && (
                        <button
                            onClick={() => setOffset(0)}
                            className="px-2 py-1 text-xs border rounded hover:bg-slate-100 text-slate-500"
                            title="Jump to today"
                        >
                            Today
                        </button>
                    )}
                    <button
                        onClick={() => setOffset((o) => o + 1)}
                        className="px-2 py-1 text-sm border rounded hover:bg-slate-100"
                        title="Navigate forward"
                    >
                        ›
                    </button>
                </div>

                <Sheet>
                    <SheetTrigger asChild>
                        <button className="ml-auto px-3 py-1 text-sm border rounded hover:bg-slate-100 text-slate-600">
                            Budget Coverage
                        </button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-80 overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle>Budget Coverage</SheetTitle>
                        </SheetHeader>
                        <BudgetCoveragePanel
                            projects={activeProjects}
                            departments={departments ?? []}
                            projectId={projectId !== "all" ? projectId : undefined}
                            departmentId={departmentId !== "all" ? departmentId : undefined}
                        />
                    </SheetContent>
                </Sheet>
            </div>

            {/* ── filters ── */}
            <div className="flex items-center gap-3 flex-wrap">
                <Select
                    value={departmentId}
                    onValueChange={(v) => {
                        setDepartmentId(v);
                        setTeamId("all");
                    }}
                >
                    <SelectTrigger className="w-44">
                        <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Departments</SelectItem>
                        {departments?.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                                {d.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={teamId} onValueChange={setTeamId}>
                    <SelectTrigger className="w-40">
                        <SelectValue placeholder="Team" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Teams</SelectItem>
                        {teams?.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger className="w-44">
                        <SelectValue placeholder="Project" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Projects</SelectItem>
                        {projects?.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                                {p.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={groupByTeam} onCheckedChange={(v) => setGroupByTeam(Boolean(v))} />
                    Group by team
                </label>
            </div>

            {/* ── timeline table ── */}
            {isLoading ? (
                <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                    ))}
                </div>
            ) : (
                <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b">
                                <th className="text-left px-3 py-2 font-medium text-slate-700 w-48 sticky left-0 bg-slate-50">
                                    Person
                                </th>
                                <th className="text-left px-3 py-2 font-medium text-slate-700 w-32"></th>
                                {columns.map((col) => (
                                    <th key={col.key} className="text-center px-2 py-2 font-medium text-slate-700 w-24">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* ── individual rows ── */}
                            {groupByTeam
                                ? grouped.map(({ teamName, teamId: tid, rows }) => (
                                      <React.Fragment key={tid}>
                                          <tr className="bg-slate-100">
                                              <td
                                                  colSpan={2 + columns.length}
                                                  className="px-3 py-1 font-semibold text-slate-600 text-xs uppercase tracking-wide"
                                              >
                                                  {teamName}
                                              </td>
                                          </tr>
                                          {rows.map((ind) => (
                                              <IndividualRows
                                                  key={ind.id}
                                                  individual={ind}
                                                  columns={columns}
                                                  projectColorMap={projectColorMap}
                                                  projectFilter={projectId !== "all" ? projectId : undefined}
                                                  onOpenModal={setModalState}
                                              />
                                          ))}
                                      </React.Fragment>
                                  ))
                                : individuals.map((ind) => (
                                      <IndividualRows
                                          key={ind.id}
                                          individual={ind}
                                          columns={columns}
                                          projectColorMap={projectColorMap}
                                          projectFilter={projectId !== "all" ? projectId : undefined}
                                          onOpenModal={setModalState}
                                      />
                                  ))}

                            {individuals.length === 0 && (
                                <tr>
                                    <td colSpan={2 + columns.length} className="text-center py-8 text-slate-400">
                                        No individuals found
                                    </td>
                                </tr>
                            )}

                            {/* ── capacity summary ── */}
                            <tr className="bg-slate-100 border-t-2 border-slate-300">
                                <td
                                    colSpan={2}
                                    className="px-3 py-1 font-semibold text-xs uppercase tracking-wide text-slate-600 sticky left-0 bg-slate-100"
                                >
                                    Capacity Summary
                                </td>
                                {columns.map((col) => (
                                    <td key={col.key} />
                                ))}
                            </tr>
                            {(
                                [
                                    {
                                        label: "Working Days",
                                        key: "workingDays",
                                        format: (v: number) => v.toFixed(0),
                                    },
                                    {
                                        label: "Gross Capacity (MD)",
                                        key: "grossCapacityMd",
                                        format: (v: number) => v.toFixed(1),
                                    },
                                    {
                                        label: "Absences (MD)",
                                        key: "absencesMd",
                                        format: (v: number) => `-${v.toFixed(1)}`,
                                    },
                                    {
                                        label: "Total Capacity (MD)",
                                        key: "totalCapacityMd",
                                        format: (v: number) => v.toFixed(1),
                                    },
                                    {
                                        label: "Project Bookings (MD)",
                                        key: "totalBookingsMd",
                                        format: (v: number) => v.toFixed(1),
                                    },
                                    {
                                        label: "Gap (MD)",
                                        key: "gapMd",
                                        format: (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}`,
                                    },
                                ] as {
                                    label: string;
                                    key: keyof ColumnCapacitySummary;
                                    format: (v: number) => string;
                                }[]
                            ).map(({ label, key, format }) => (
                                <tr key={key} className="bg-slate-50 border-b">
                                    <td className="px-3 py-1 text-xs text-slate-500 sticky left-0 bg-slate-50" />
                                    <td className="px-3 py-1 text-xs text-slate-500">{label}</td>
                                    {columns.map((col) => {
                                        const summary = computeCapacitySummary(individuals, col);
                                        const value = summary[key];
                                        return (
                                            <td
                                                key={col.key}
                                                className={clsx("px-1 text-center text-xs w-24 font-medium", {
                                                    "text-red-600": key === "gapMd" && value < 0,
                                                    "text-green-700": key === "gapMd" && value >= 0,
                                                    "text-slate-600": key !== "gapMd",
                                                })}
                                            >
                                                {format(value)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── booking modal ── */}
            {modalState && (
                <BookingModal
                    state={modalState}
                    projects={activeProjects}
                    departments={departments ?? []}
                    onConfirm={handleModalConfirm}
                    onDelete={modalState.mode === "edit" ? handleModalDelete : undefined}
                    onClose={() => setModalState(null)}
                />
            )}
        </div>
    );
};
